/**
 * @flow
 * @format
 */
/* global window */

import React from 'react';
import Hashids from 'hashids';
import snakeCase from 'lodash/snakeCase';
import isString from 'lodash/isString';
import { clone, setWith, curry } from 'lodash/fp';

import * as HtmlToReact from 'html-to-react';
import * as d3 from 'd3';
import { fork, take, cancel, select } from 'redux-saga/effects';

import type { Selector } from 'reselect';
// import canvg from 'canvg';

import { defaults } from './constants';

// const remark = require('remark');
// const remarkHtml = require('remark-html');
// const remarkSupSuper = require('remark-sub-super');
const canvg = require('../extra/canvg');

type ReactEl = ?React$Element<*>;
const htmlToReactParser: () => ReactEl = new HtmlToReact.Parser(React);
// const markDownRenderer = remark().use(remarkHtml).use(remarkSupSuper);

/**
 * Round a number to the given places.
 *
 * @export
 * @param {number | string} value
 * @param {number | string} decimals
 * @returns {number}
 */
export function placeRound(
  value: number | string,
  decimals: number | string = defaults.DECIMAL_PLACES,
): number {
  return +`${Math.round(+`${value}e+${decimals}`)}e-${decimals}`;
}

/**
 * Calculate the mid point between two angles
 *
 * @export
 * @param {number} startAngle
 * @param {number} endAngle
 * @returns {number}
 */
export function midAngle(startAngle: number, endAngle: number): number {
  return startAngle + (endAngle - startAngle) / 2;
}

/**
 *
 *
 * @export
 * @param {(number | string)} n
 * @returns {boolean}
 */
export function isNumeric(n: ?(number | string)): boolean {
  // eslint-disable-next-line no-restricted-globals
  return !isNaN(parseFloat(n)) && isFinite(n);
}

/**
 * Calculates the percent of the whole a part is.
 *
 * @export
 * @param {number} part
 * @param {number} whole
 * @returns {number}
 */
export function toPercent(part: number, whole: number): number {
  return part / whole * 100;
}

function createMarkup(string: string): React$Node {
  try {
    return htmlToReactParser.parse(string);
  } catch (err) {
    return null;
  }
}

/**
 * Create React friendly html element from a string without relying on dangerouslySetInnerHTML
 *
 * @export
 * @param {string} string
 * @returns {React$Node}
 */
export function createHTMLMarkup(
  string: ?string,
  wrapper: ?string = 'span',
  attr: string = '',
) {
  if (!string || !isString(wrapper)) return null;

  if (wrapper === null) {
    return createMarkup(string);
  }

  return createMarkup(`<${wrapper} ${attr}>${string}</${wrapper}>`);
}

/**
 *
 *
 * @export
 * @param {string} [string]
 */
// export function createMdMarkup(string?: string) {
//   if (!_.isString(string)) return null;
//   let html = null;
//   try {
//     const markup = markDownRenderer.process(string, (err, file) => { html = file; console.log(file); });
//     console.log(markup);
//   } catch (err) {
//     return null;
//   }
//   return html;
// }

/**
 * Create React friendly svg element from a string without relying on dangerouslySetInnerHTML
 *
 * @export
 * @param {string} string
 * @returns {SVGElement}
 */
export function createSVGMarkup(
  string: string,
  wrapper: string = 'g',
  attr?: string = '',
) {
  if (!string || (isString(wrapper) && wrapper === '')) return null;
  let fixString = string;
  fixString = string.replace(/stroke-width/g, 'strokeWidth');
  fixString = fixString.replace(/font-size/g, 'fontSize');
  fixString = fixString.replace(/font-weight/g, 'fontWeight');
  fixString = fixString.replace(/text-anchor/g, 'textAnchor');
  fixString = fixString.replace(/viewbox/g, 'viewBox');
  return createMarkup(`<${wrapper} ${attr}>${fixString}</${wrapper}>`);
}

/**
 * Check if a value is between two numbers
 *
 * @export
 * @param {number} value
 * @param {number} low
 * @param {number} high
 * @param {boolean} [inclusive=false]
 * @returns {boolean}
 */
export function isBetween(
  value: number,
  low: number,
  high: number,
  inclusive: boolean = false,
): boolean {
  const min = Math.min(low, high);
  const max = Math.max(low, high);
  return inclusive ? value >= min && value <= max : value > min && value < max;
}

/**
 * A class to handle unique id generation.
 * https://github.com/ivanakimov/hashids.js
 *
 * @export
 * @class HashId
 */

export class HashId {
  count: number;
  hashids: typeof Hashids;

  constructor() {
    this.hashids = new Hashids(
      'Boost',
      8,
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890',
    );
    this.count = 0;
  }
  /**
   * Returns the next id
   *
   * @returns {string}
   * @memberOf HashId
   */
  nextId(): string {
    this.count += 1;
    return this.hashids.encode(this.count);
  }

  /**
   * Returns encoded value
   *
   * @returns {string}
   * @memberOf HashId
   */
  encode(value: string | number) {
    return this.hashids.encode(value);
  }
}

/**
 * Detect IE
 * returns version of IE or false, if browser is not Internet Explorer
 * Modified from https://codepen.io/gapcode/pen/vEJNZN
 *
 * @exports
 * @param {string} User Agent to test; defaults to window.navigator.userAgent
 * @returns boolean
 */
export function isBrowserIE(ua?: string = window.navigator.userAgent): boolean {
  /* eslint-disable max-len */
  // IE 10: 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';
  // IE 11: 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';
  // Edge 12 (Spartan): 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';
  // Edge 13: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';
  /* eslint-enable max-len */

  let version = 0;

  const msie = ua.indexOf('MSIE ');
  if (msie > 0) {
    // IE 10 or older => return version number
    version = parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
  }

  const trident = ua.indexOf('Trident/');
  if (trident > 0) {
    // IE 11 => return version number
    const rv = ua.indexOf('rv:');
    version = parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
  }

  const edge = ua.indexOf('Edge/');
  if (edge > 0) {
    // Edge (IE 12+) => return version number
    version = parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
  }

  return version > 0;
}

/**
 * @export
 * @param {string} ua
 * @returns boolean
 */
export function isBrowserFF(ua?: string = window.navigator.userAgent): boolean {
  return /Firefox\/([0-9]+)\./.test(ua);
}

export function generateCVSBlob(data: Array<Array<string>>): window.Blob {
  const csv = data.map((d: Array<string>) =>
    d.map((f: string) => JSON.stringify(f)).join(','),
  );

  /**
   * The byte-order-marker (\ufeff) needs to be prepended to automatically
   * set the UTF character mode when importing.
   * https://stackoverflow.com/a/17879474
   */
  const forceUTF8 = `\ufeff${csv.join('\r\n')}`;
  return new window.Blob([forceUTF8], { type: 'text/csv' });
}

export function downloadBlob(blob: Blob, fileName?: string = 'untitled') {
  // ie11 throws a security error when trying to download blobs like real browers.
  if (isBrowserIE() && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, fileName);
  } else {
    const anchor = window.document.createElement('a');
    if (anchor) {
      anchor.href = window.URL.createObjectURL(blob);
      anchor.download = fileName;
      window.document.body.appendChild(anchor);
      anchor.click();
      window.document.body.removeChild(anchor);
    }
  }
}

export function downloadSVGImage(data: string, fileName: string) {
  const width = 640;
  const height = 350;
  const exportName = `${fileName}.png`;

  const svg = data.replace(/\n\s+/g, '').replace(/&mu;/g, 'μ');

  const canvas: HTMLCanvasElement = d3
    .select('body')
    .append('canvas')
    .attr('hidden', true)
    .node();

  const forceClick = (uri: string) => {
    const a = window.document.createElement('a');
    a.setAttribute('href', uri);
    a.setAttribute('target', '_blank');
    a.setAttribute('download', exportName);

    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
  };

  const ctx = canvas.getContext('2d', { preserveDrawingBuffer: true });

  canvas.width = width * 2;
  canvas.height = height * 2;

  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, width * 2, height * 2);

  canvg(canvas, svg, {
    ignoreMouse: true,
    ignoreAnimation: true,
  });

  if ('msToBlob' in canvas) {
    // $FlowIgnore
    window.navigator.msSaveOrOpenBlob(canvas.msToBlob(), exportName);
    return;
  }

  forceClick(canvas.toDataURL('image/png'));
}

/**
 * Join URL paths
 * http://stackoverflow.com/a/29855282
 *
 * @export
 * @param {string[]} parts
 * @param {string} separator
 */
export function pathJoin(parts: any[], separator: string = '/') {
  const replace = new RegExp(`${separator}{1,}`, 'g');

  return parts.join(separator).replace(replace, separator);
}

/**
 * Branch helper function
 * @export
 * @param {boolean} predicate
 * @param {any} Comp1
 * @param {any} Comp2
 * @returns Comp1 || Comp2
 */
export function branch<L, R>(
  predicate: boolean,
  left: L,
  right?: R | null = null,
): L | R | null {
  return predicate ? left : right;
}

/**
 * Takes a saga and a callback and spawns a new task
 * that will cancel based on take(cb)
 *
 * @param {Generator} fn
 * @param {Function | [] | string} cb
 * @returns {Generator}
 */
export function* asyncCancelOnTake(
  fn: any,
  args: any,
  cb: string | Array<string> | ((action: Object) => boolean),
): Generator<*, *, *> {
  const task = yield fork(fn, args);
  yield take(cb);
  yield cancel(task);
}

/**
 * Helper to Wait on a specfic selector condition before returning
 * https://goshakkk.name/detect-state-change-redux-saga/
 *
 * @param {selector} selector
 */
export function* waitFor(
  selector: Selector<*, *, boolean>,
): Generator<*, *, *> {
  let cond: boolean = yield select(selector);
  if (cond) return;

  while (true) {
    yield take('*');
    cond = yield select(selector);
    if (cond) return;
  }
}

/**
 * Count decimal places of a given number or string
 * http://stackoverflow.com/a/17369384
 * http://stackoverflow.com/a/10454560
 * @param {number | string} value
 */
export function countDecimals(value: number | string) {
  if (typeof value === 'string') {
    const match = value.match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
    if (match) {
      return Math.max(
        0,
        // Number of digits right of decimal point.
        (match[1] ? match[1].length : 0) -
          // Adjust for scientific notation.
          (match[2] ? +match[2] : 0),
      );
    }
  }

  if (typeof value !== 'string') {
    if (Math.floor(value) !== value) {
      return value.toString().split('.')[1].length || 0;
    }
  }

  return 0;
}

export function makeFileName(filename: string, ext?: string) {
  if (!ext) return snakeCase(filename);
  return [snakeCase(filename), ext].join('.');
}

/**
 * nope.
 */
export const noop = () => {};

/**
 * Small UUID funciton
 * https://www.npmjs.com/package/uuid
 *
 * @export
 * @param {uuid} a
 * @returns
 */
/* eslint-disable */
// prettier-ignore
// $FlowIgnore
export function uuid(a) { return a ? (a ^ Math.random() * 16 >> a / 4).toString(16) : ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, uuid) }
/* eslint-enable */

/**
 * Find and replace multiple strings with the passed object.
 * https://stackoverflow.com/a/15604206
 *
 * @export
 * @param {string} str
 * @param {Object} mapObj
 * @returns string
 */
export function replaceAll(str: string, mapObj: { [key: string]: string }) {
  const re = new RegExp(Object.keys(mapObj).join('|'), 'gi');
  return str.replace(re, matched => mapObj[matched]);
}

/**
 * A simple string title capilization function
 *
 * @export
 * @param {string} str
 * @returns string
 */
export function toTitleCase(str?: string) {
  // prettier-ignore
  const lowers = [
    'a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor',
    'as', 'at', 'by', 'in', 'of', 'on', 'to', 'vs.',
  ];
  if (typeof str !== 'string') return null;
  return str.replace(/\w\S*/g, txt => {
    if (lowers.indexOf(txt) !== -1) return txt;
    return `${txt.charAt(0).toUpperCase()}${txt.substr(1).toLowerCase()}`;
  });
}

/**
 * Gets an element's potition from the top of the page
 * https://stackoverflow.com/a/24829409
 *
 * @export
 * @param {HTMLElement} el
 * @returns {| x: number, y: number |}
 */
export function getPosition(
  element: Element | Node | HTMLElement,
): {| x: number, y: number |} {
  let xPos = 0;
  let yPos = 0;

  if (element instanceof HTMLElement) {
    while (element) {
      // $FlowIgnore
      xPos += element.offsetLeft - element.scrollLeft + element.clientLeft;
      // $FlowIgnore
      yPos += element.offsetTop - element.scrollTop + element.clientTop;
      /* eslint-disable no-param-reassign */
      // $FlowIgnore
      element = element.offsetParent;
      /* eslint-enable */
    }
  }

  return { x: xPos, y: yPos };
}

// Pulled from react-compat
// https://github.com/developit/preact-compat/blob/7c5de00e7c85e2ffd011bf3af02899b63f699d3a/src/index.js#L349
export function shallowDiffers(a: Object, b: Object) {
  /* eslint-disable no-restricted-syntax, prefer-const */
  for (let i in a) if (!(i in b)) return true;
  for (let i in b) if (a[i] !== b[i]) return true;
  /* eslint-enable no-restricted-syntax, prefer-const */
  return false;
}

/**
 * Recursive deep diff two things
 * https://github.com/facebook/react-native/blob/master/Libraries/Utilities/differ/deepDiffer.js
 * @param {*} one
 * @param {*} two
 * @returns {bool} true if different, false if equal
 */
export function deepDiffer(one: any, two: any): boolean {
  /* eslint-disable no-restricted-syntax, prefer-const */
  if (one === two) {
    // Short circuit on identical object references instead of traversing them.
    return false;
  }
  if (typeof one === 'function' && typeof two === 'function') {
    // We consider all functions equal
    return false;
  }
  if (typeof one !== 'object' || one === null) {
    // Primitives can be directly compared
    return one !== two;
  }
  if (typeof two !== 'object' || two === null) {
    // We know they are different because the previous case would have triggered
    // otherwise.
    return true;
  }
  if (one.constructor !== two.constructor) {
    return true;
  }
  if (Array.isArray(one)) {
    // We know two is also an array because the constructors are equal
    const len = one.length;
    if (two.length !== len) {
      return true;
    }
    for (let ii = 0; ii < len; ii += 1) {
      if (deepDiffer(one[ii], two[ii])) {
        return true;
      }
    }
  } else {
    for (let key in one) {
      if (deepDiffer(one[key], two[key])) {
        return true;
      }
    }
    for (let twoKey in two) {
      // The only case we haven't checked yet is keys that are in two but aren't
      // in one, which means they are different.
      if (one[twoKey] === undefined && two[twoKey] !== undefined) {
        return true;
      }
    }
  }
  return false;
  /* eslint-enable no-restricted-syntax, prefer-const */
}

/**
 * Performs equality by iterating through keys on an object and returning
 * false when any key has values which are not strictly equal between
 * objA and objB. Returns true when the values of all keys are strictly equal.
 *
 * @return {boolean}
 */
export function shallowEqual(objA: any, objB: any) {
  /* eslint-disable no-restricted-syntax, no-prototype-builtins */
  if (objA == null || objB == null) return false;

  if (objA === objB) {
    return true;
  }
  let key;
  // Test for A's keys different from B.
  for (key in objA) {
    // prettier-ignore
    if (objA.hasOwnProperty(key) &&
      (!objB.hasOwnProperty(key) || objA[key] !== objB[key])) {
      return false;
    }
  }
  // Test for B's keys missing from A.
  for (key in objB) {
    // prettier-ignore
    if (objB.hasOwnProperty(key) && !objA.hasOwnProperty(key)) {
      return false;
    }
  }
  return true;
  /* eslint-enable no-restricted-syntax, prefer-const */
}

/**
 * A curried helper function to set an object path without mutation.
 * This is needed because lodash.set mutates the object.
 *
 * ! Once the project is switched to typesript this will be handled differently.
 *
 * @param {string} path
 * @param {any} value
 * @param {Object} obj
 */
// $FlowIgnore
export const setIn = curry((path, value, obj) =>
  setWith(clone, path, value, clone(obj)),
);



// WEBPACK FOOTER //
// ./src/utils/index.js